import React, { useState } from "react";
import { 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  CardActions, 
  Button, 
  Box,
  Chip,
  IconButton,
  Tooltip,
  Zoom,
  Fade,
  alpha,
  Modal,
  Backdrop,
  Grid,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SecurityIcon from "@mui/icons-material/Security";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CloseIcon from "@mui/icons-material/Close";
import DiamondIcon from "@mui/icons-material/Diamond";
import SendIcon from "@mui/icons-material/Send";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

const CoinCard = ({ coin, onPriceInquiry }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    quantity: '1'
  });

  // Fallback image in case the coin image fails to load
  const fallbackImage = "https://via.placeholder.com/400x400?text=Rare+Coin";

  const handleQuickViewOpen = () => setQuickViewOpen(true);
  const handleQuickViewClose = () => setQuickViewOpen(false);

  const handleInquiryFormChange = (e) => {
    setInquiryForm({
      ...inquiryForm,
      [e.target.name]: e.target.value
    });
  };

  const sendEmailInquiry = (isAddToCart = true) => {
    // Prepare email subject
    const subject = isAddToCart 
      ? `Purchase Inquiry: ${coin.name} (${coin.year} ${coin.grade})`
      : `Price Inquiry: ${coin.name} (${coin.year} ${coin.grade})`;
    
    // Prepare email body
    const body = `
      ${isAddToCart ? 'Purchase Request' : 'Price Inquiry'} Details:
      
      Coin Information:
      -----------------
      Coin Name: ${coin.name}
      Year: ${coin.year}
      Grade: ${coin.grade}
      Mint: ${coin.mint || 'Not specified'}
      Metal: ${coin.metal || 'Not specified'}
      Price: ${coin.price}
      Certification: ${coin.certification || 'PCGS/NGC Certified'}
      
      ${isAddToCart ? `
      Customer Information:
      --------------------
      Full Name: ${inquiryForm.name || '[Not provided]'}
      Email: ${inquiryForm.email || '[Not provided]'}
      Phone: ${inquiryForm.phone || '[Not provided]'}
      Quantity: ${inquiryForm.quantity || '1'}
      
      Message/Notes:
      --------------
      ${inquiryForm.message || 'Interested in purchasing this coin. Please provide payment and shipping details.'}
      
      Additional Information:
      -----------------------
      • Request sent from: Website
      • Date: ${new Date().toLocaleString()}
      • Coin ID: ${coin.id || 'N/A'}
      ` : `
      Inquiry Details:
      ----------------
      This customer is interested in this coin and would like more information about pricing and availability.
      
      Coin URL: ${window.location.href}
      `}
    `;

    // Create mailto link
    const mailtoLink = `mailto:john.asfar@worldwidecorporation.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    // Show success message
    setSnackbarMessage(isAddToCart 
      ? `Purchase inquiry for ${coin.name} sent! Our team will contact you within 24 hours.` 
      : `Price inquiry for ${coin.name} sent! We'll respond shortly.`);
    setSnackbarOpen(true);
    
    // Close dialogs
    if (isAddToCart) {
      setInquiryDialogOpen(false);
      setQuickViewOpen(false);
    }
    
    // Reset form after 2 seconds
    setTimeout(() => {
      setInquiryForm({
        name: '',
        email: '',
        phone: '',
        message: '',
        quantity: '1'
      });
    }, 2000);
  };

  const handleButtonClick = () => {
    setInquiryDialogOpen(true);
  };

  const handleQuickViewButtonClick = () => {
    setQuickViewOpen(false);
    setInquiryDialogOpen(true);
  };

  const handleInquirySubmit = () => {
    sendEmailInquiry(coin.price !== "POA");
  };

  return (
    <>
      <Card 
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{ 
          height: 460,
          width: '100%',
          maxWidth: 340,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backgroundColor: '#FFFFFF',
          cursor: 'pointer',
          border: '1px solid rgba(158, 158, 158, 0.15)',
          transition: 'all 0.3s ease',
          margin: '0 auto',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
            borderColor: 'primary.main',
          },
        }}
      >
        {/* Diamond Badge - if coin has diamond */}
        {coin.hasDiamond && (
          <Zoom in={true} style={{ transitionDelay: '100ms' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                backgroundColor: alpha('#C6A75E', 0.95),
                borderRadius: '20px',
                px: 1,
                py: 0.3,
                display: 'flex',
                alignItems: 'center',
                gap: 0.3,
                boxShadow: '0 2px 8px rgba(198,167,94,0.3)',
              }}
            >
              <DiamondIcon sx={{ color: '#FFFFFF', fontSize: 12 }} />
              <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.6rem' }}>
                DIAMOND
              </Typography>
            </Box>
          </Zoom>
        )}

        {/* Certification Badge */}
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
          <Box
            sx={{
              position: 'absolute',
              top: coin.hasDiamond ? 55 : 12,
              right: 12,
              zIndex: 2,
              backgroundColor: alpha('#FFFFFF', 0.98),
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '1.5px solid',
              borderColor: 'primary.main',
            }}
          >
            <Tooltip title="PCGS Certified" arrow placement="top">
              <VerifiedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            </Tooltip>
          </Box>
        </Zoom>

        {/* Like Button */}
        <IconButton
          onClick={() => setIsLiked(!isLiked)}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 2,
            backgroundColor: alpha('#FFFFFF', 0.98),
            padding: '6px',
            width: 36,
            height: 36,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1.5px solid',
            borderColor: isLiked ? 'primary.main' : 'transparent',
            '&:hover': {
              backgroundColor: '#FFFFFF',
              borderColor: 'primary.main',
            },
          }}
        >
          {isLiked ? 
            <FavoriteIcon sx={{ color: 'primary.main', fontSize: 18 }} /> : 
            <FavoriteBorderIcon sx={{ color: '#616161', fontSize: 18 }} />
          }
        </IconButton>

        {/* Image Container */}
        <Box 
          sx={{ 
            position: 'relative', 
            overflow: 'hidden',
            backgroundColor: '#F8F8F8',
            height: 220,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid rgba(158, 158, 158, 0.08)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1.5,
            }}
          >
            <CardMedia
              component="img"
              image={!imageError ? coin.image : fallbackImage}
              alt={coin.name}
              onError={() => setImageError(true)}
              sx={{
                maxWidth: '90%',
                maxHeight: '90%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                transition: 'transform 0.5s ease',
                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                borderRadius: 1,
              }}
            />
          </Box>
          
          {/* Quick View Overlay */}
          <Fade in={isHovered}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isHovered ? 1 : 0,
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={handleQuickViewOpen}
                startIcon={<RemoveRedEyeIcon sx={{ fontSize: 16 }} />}
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#000000',
                  fontSize: '0.85rem',
                  padding: '8px 20px',
                  boxShadow: '0 4px 12px rgba(198,167,94,0.3)',
                }}
              >
                Quick View
              </Button>
            </Box>
          </Fade>
        </Box>

        <CardContent sx={{ 
          flexGrow: 1, 
          p: 2,
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}>
          {/* Title */}
          <Typography 
            variant="subtitle1"
            sx={{ 
              fontWeight: 600,
              fontSize: '1rem',
              mb: 1,
              color: '#2D2D2D',
              lineHeight: 1.3,
              height: '2.4rem',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              width: '100%',
            }}
          >
            {coin.name}
          </Typography>
          
          {/* Grade and Year Chips */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            mb: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
            width: '100%',
          }}>
            <Chip 
              label={coin.grade}
              size="small"
              sx={{ 
                height: 24,
                fontSize: '0.75rem',
                backgroundColor: alpha('#C6A75E', 0.05),
                border: '1.5px solid',
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 600,
              }}
            />
            <Chip 
              label={coin.year}
              size="small"
              sx={{ 
                height: 24,
                fontSize: '0.75rem',
                backgroundColor: '#F5F5F5',
                color: '#616161',
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Mint if available */}
          {coin.mint && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#616161',
                mb: 0.5,
                display: 'block',
                fontSize: '0.65rem',
              }}
            >
              {coin.mint}
            </Typography>
          )}

          {/* Features Icons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            mb: 1,
            justifyContent: 'center',
            width: '100%',
          }}>
            <Tooltip title="Insured Shipping" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <LocalShippingIcon sx={{ color: 'primary.main', fontSize: 14 }} />
                <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem', fontWeight: 500 }}>
                  Insured
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Secure Transaction" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <SecurityIcon sx={{ color: 'primary.main', fontSize: 14 }} />
                <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem', fontWeight: 500 }}>
                  Escrow
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {/* Description */}
          <Typography 
            variant="caption"
            sx={{ 
              color: '#616161',
              mb: 1,
              fontSize: '0.7rem',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2rem',
              width: '100%',
            }}
          >
            {coin.description}
          </Typography>

          {/* Price */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            mt: 'auto',
            width: '100%',
          }}>
            <Typography variant="caption" sx={{ color: '#9E9E9E', fontSize: '0.6rem', letterSpacing: '0.5px', mb: 0.2 }}>
              {coin.price === "POA" ? 'PRICE ON APPLICATION' : 'CURRENT PRICE'}
            </Typography>
            <Typography variant="h6" sx={{
              color: coin.price === "POA" ? 'warning.main' : 'primary.main', 
              fontWeight: 700,
              fontSize: '1.2rem',
              lineHeight: 1.2,
            }}>
              {coin.price}
            </Typography>
          </Box>
        </CardContent>

        <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center', width: '100%' }}>
          <Button 
            variant="contained" 
            size="small"
            onClick={handleButtonClick}
            startIcon={coin.price === "POA" ? <SendIcon /> : <ShoppingCartIcon />}
            sx={{ 
              py: 0.8,
              px: 3,
              fontSize: '0.8rem',
              backgroundColor: coin.price === "POA" ? 'warning.main' : 'primary.main',
              color: '#000000',
              minWidth: 160,
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: coin.price === "POA" ? 'warning.dark' : 'primary.dark',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(198,167,94,0.2)',
              },
            }}
          >
            {coin.price === "POA" ? 'Inquire Now' : 'Add to Cart'}
          </Button>
        </CardActions>
      </Card>

      {/* Quick View Modal - with Close Button */}
      <Modal
        open={quickViewOpen}
        onClose={handleQuickViewClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: alpha('#000000', 0.7),
            backdropFilter: 'blur(4px)',
          }
        }}
      >
        <Fade in={quickViewOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 800,
              bgcolor: '#FFFFFF',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              borderRadius: 2,
              overflow: 'hidden',
              outline: 'none',
            }}
          >
            {/* Close Button - Top Right Corner */}
            <IconButton
              onClick={handleQuickViewClose}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 10,
                backgroundColor: alpha('#FFFFFF', 0.95),
                padding: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Grid container>
              {/* Image Section */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    height: { xs: 250, md: 350 },
                    backgroundColor: '#F8F8F8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Box
                    component="img"
                    src={!imageError ? coin.image : fallbackImage}
                    alt={coin.name}
                    onError={() => setImageError(true)}
                    sx={{
                      maxWidth: '90%',
                      maxHeight: '90%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: 1,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                    }}
                  />
                </Box>
              </Grid>
              
              {/* Details Section */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: '#2D2D2D', fontSize: '1.3rem' }}>
                    {coin.name}
                  </Typography>
                  
                  <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Chip 
                      label={coin.grade}
                      size="small"
                      sx={{
                        backgroundColor: alpha('#C6A75E', 0.1),
                        border: '1.5px solid',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        fontWeight: 600,
                        height: 24,
                        fontSize: '0.75rem',
                      }}
                    />
                    <Chip 
                      label={coin.year}
                      size="small"
                      sx={{
                        backgroundColor: '#F5F5F5',
                        color: '#616161',
                        fontWeight: 600,
                        height: 24,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Stack>

                  <Typography variant="body2" sx={{ mb: 2, color: '#616161', lineHeight: 1.6, fontSize: '0.85rem' }}>
                    {coin.description}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#9E9E9E', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                      Specifications
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#2D2D2D', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                          Metal
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem' }}>
                          {coin.metal}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#2D2D2D', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                          Grade
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem' }}>
                          {coin.grade}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#2D2D2D', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                          Year
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem' }}>
                          {coin.year}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#2D2D2D', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
                          Mint
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#616161', fontSize: '0.7rem' }}>
                          {coin.mint || 'Various'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ color: coin.price === "POA" ? 'warning.main' : 'primary.main', fontWeight: 700, fontSize: '1.5rem' }}>
                      {coin.price}
                    </Typography>
                    {coin.price === "POA" ? (
                      <Typography variant="caption" sx={{ color: '#9E9E9E', fontSize: '0.65rem' }}>
                        Price on Application - Please inquire
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#9E9E9E', fontSize: '0.65rem' }}>
                        Including premium & insurance
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button 
                      variant="contained" 
                      size="small"
                      fullWidth
                      onClick={handleQuickViewButtonClick}
                      startIcon={coin.price === "POA" ? <SendIcon /> : <ShoppingCartIcon />}
                      sx={{ 
                        py: 1,
                        backgroundColor: coin.price === "POA" ? 'warning.main' : 'primary.main',
                        color: '#000000',
                        fontSize: '0.8rem',
                        '&:hover': {
                          backgroundColor: coin.price === "POA" ? 'warning.dark' : 'primary.dark',
                        }
                      }}
                    >
                      {coin.price === "POA" ? 'Inquire Now' : 'Add to Cart'}
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      fullWidth
                      onClick={handleQuickViewClose}
                      sx={{ 
                        py: 1,
                        fontSize: '0.8rem',
                        borderColor: alpha('#9E9E9E', 0.3),
                      }}
                    >
                      Close
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Modal>

      {/* Inquiry/Purchase Dialog */}
      <Dialog
        open={inquiryDialogOpen}
        onClose={() => setInquiryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: alpha('#9E9E9E', 0.1) }}>
          <IconButton
            onClick={() => setInquiryDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#616161',
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h5" color="primary" gutterBottom>
            {coin.price === "POA" ? 'Price Inquiry' : 'Purchase Request'}
          </Typography>
          <Typography variant="body2" color="#616161">
            {coin.price === "POA" 
              ? 'Request pricing information for this rare coin'
              : 'Complete the form to proceed with your purchase'
            }
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3, p: 2, backgroundColor: alpha('#C6A75E', 0.05), borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D' }}>
              Coin Details
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
              {coin.name}
            </Typography>
            <Typography variant="caption" color="#616161">
              {coin.year} | {coin.grade} | {coin.metal}
            </Typography>
            {coin.price !== "POA" && (
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mt: 1 }}>
                Price: {coin.price}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            label="Full Name"
            name="name"
            value={inquiryForm.name}
            onChange={handleInquiryFormChange}
            margin="normal"
            required
            placeholder="Enter your full name"
          />
          
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={inquiryForm.email}
            onChange={handleInquiryFormChange}
            margin="normal"
            required
            placeholder="your@email.com"
          />
          
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={inquiryForm.phone}
            onChange={handleInquiryFormChange}
            margin="normal"
            required
            placeholder="+1 (555) 000-0000"
          />
          
          {coin.price !== "POA" && (
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={inquiryForm.quantity}
              onChange={handleInquiryFormChange}
              margin="normal"
              InputProps={{ inputProps: { min: 1, max: 10 } }}
              helperText="Maximum quantity available: 10"
            />
          )}
          
          <TextField
            fullWidth
            label="Additional Message (Optional)"
            name="message"
            value={inquiryForm.message}
            onChange={handleInquiryFormChange}
            margin="normal"
            multiline
            rows={3}
            placeholder={coin.price === "POA" 
              ? "Any specific questions about this coin?"
              : "Any special requests or questions about your purchase?"
            }
          />
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button
            onClick={() => setInquiryDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleInquirySubmit}
            variant="contained"
            startIcon={coin.price === "POA" ? <SendIcon /> : <ShoppingCartIcon />}
            disabled={!inquiryForm.name || !inquiryForm.email || !inquiryForm.phone}
          >
            {coin.price === "POA" ? 'Send Inquiry' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CoinCard;